import {
  CartItem,
  CartItemDesign,
  CartItemLogo,
} from "../order-create/models/CartItem";
import { BaseLogo, LogoType } from "../../../core/models/entities/BaseLogo";
import { NumberLogoStorage } from "../../../data/models/responses/LogoDto";
import { VerifyStorageRowDto } from "../../../data/models/responses/VerifyStorageResponseDto";
import { Order, Packaging } from "../../../core/models/entities/Order";

// Hard cap on how long the confirmation step waits for the storage verify.
// The step renders immediately either way; a late answer is simply dropped.
export const STORAGE_VERIFY_TIMEOUT_MS = 8000;

export interface StorageDraw {
  logoId: number;
  title: string;
  /**
   * Total DELIVERY demand of this logo across the cart: pieces, or digit
   * applications for number logos. Storage quantities never draw.
   */
  deliveryQty: number;
  /** The count in play: the logo's own, or the digit sum for number logos. */
  inStorage: number;
  /**
   * What the draft draws from storage for this logo: min(demand, count),
   * per digit for number logos (buildLocalOrderDraft's rule).
   */
  draw: number;
}

export interface StorageChangeWarning {
  logoId: number;
  title: string;
  /** The count the customer saw (the cart's own copy of the logo). */
  cached: number;
  /** The count NAV reports now. */
  fresh: number;
  /**
   * How many more pieces the order produces instead of drawing from storage:
   * the draw before minus the draw after. Independent of any storage
   * quantity on the same line, so it matches the rebuilt draft either way.
   */
  produced: number;
}

/**
 * Verified counts by logo id. Number logos are included only when their
 * digit rows were delivered - the same rule patchStorageCounts applies, so
 * what is announced is always what is patched.
 */
export interface FreshStorageCounts {
  scalar: Map<number, number>;
  digits: Map<number, NumberLogoStorage[]>;
  /**
   * The storage row's stamp per verified logo (the API's storageUpdatedAtUtc,
   * falling back to the row's verifiedAtUtc for an older API), so the cart
   * shows "Stock updated just now" after a verify.
   */
  updatedAt: Map<number, string | null>;
}

export interface PackagingFeeInput {
  itemNo: string;
  price: number;
  name: string;
  quantity: number;
}

const SPECIFIC_PACKAGING_TITLE_SUFFIX = " (QTY will be adjusted)";

function isLogoItem(item: CartItem): item is CartItemLogo {
  return item.type === "Logo";
}

function isDesignItem(item: CartItem): item is CartItemDesign {
  return item.type === "Design";
}

/**
 * Distinct logo ids of everything in the cart, in first-seen order: standalone
 * logos, the design's own logos and the per-instance logo specs (which can
 * reference a logo the design list no longer carries).
 */
export function collectCartLogoIds(items: CartItem[]): number[] {
  const seen = new Set<number>();
  const ids: number[] = [];
  const add = (id: number | undefined | null) => {
    if (id == null || !(id > 0) || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  };
  items.forEach((item) => {
    if (isLogoItem(item)) {
      add(item.baseLogo?.id ?? item.logoId);
    } else if (isDesignItem(item)) {
      item.design?.designLogos?.forEach((dl) => add(dl.logoId ?? dl.logo?.id));
      item.instances?.forEach((inst) =>
        inst.logos?.forEach((l) => add(l.logoId ?? l.baseLogo?.id))
      );
    }
  });
  return ids;
}

export function freshCountsFromRows(
  rows: VerifyStorageRowDto[]
): FreshStorageCounts {
  const scalar = new Map<number, number>();
  const digits = new Map<number, NumberLogoStorage[]>();
  const updatedAt = new Map<number, string | null>();
  rows.forEach((row) => {
    if (!row.verified) return;
    if (row.isNumberLogo) {
      if (!row.digits || row.digits.length === 0) return;
      digits.set(
        row.logoId,
        row.digits.map((d) => ({ value: d.value, inStorage: d.inStorage }))
      );
    }
    scalar.set(row.logoId, row.inStorage);
    updatedAt.set(row.logoId, row.storageUpdatedAtUtc ?? row.verifiedAtUtc ?? null);
  });
  return { scalar, digits, updatedAt };
}

interface DrawAccumulator {
  title: string;
  logo: BaseLogo;
  demand: number;
  digitDemand: Map<string, number>;
}

/**
 * Per-logo storage draw the way the local draft computes it
 * (buildLocalOrderDraft): every delivery line takes min(quantity, what is
 * left) from ONE pool per logo - per digit for number logos, where each
 * digit of a typed value is an application of the line's quantity - which
 * totals min(demand, count) per pool. Design instances resolve the design's
 * own logo copy first, like the draft does. `fresh` swaps in the verified
 * counts without touching the items.
 */
export function computeStorageDraws(
  items: CartItem[],
  fresh?: FreshStorageCounts
): Map<number, StorageDraw> {
  const acc = new Map<number, DrawAccumulator>();

  const add = (
    logo: BaseLogo | undefined,
    qty: number,
    value: string | number | undefined,
    title?: string
  ) => {
    if (!logo || !(logo.id > 0) || !(qty > 0)) return;
    let entry = acc.get(logo.id);
    if (!entry) {
      entry = {
        title: title || logo.title || "",
        logo,
        demand: 0,
        digitDemand: new Map<string, number>(),
      };
      acc.set(logo.id, entry);
    }
    if (logo.logoType === LogoType.NumberLogo) {
      const digitDemand = entry.digitDemand;
      `${value ?? ""}`.split("").forEach((digit) => {
        digitDemand.set(digit, (digitDemand.get(digit) ?? 0) + qty);
      });
    } else {
      entry.demand += qty;
    }
  };

  items.forEach((item) => {
    if (isLogoItem(item)) {
      const logo = item.baseLogo;
      const title = logo?.title ?? item.title;
      switch (logo?.logoType) {
        case LogoType.NumberLogo:
        case LogoType.NameLogo:
          item.deliveryQuantities?.forEach((row) =>
            add(logo, row.quantity, row.label, title)
          );
          break;
        default:
          add(logo, item.toDelivery ?? 0, undefined, title);
      }
    } else if (isDesignItem(item)) {
      item.instances?.forEach((inst) => {
        inst.logos?.forEach((spec) => {
          const designLogo = item.design?.designLogos?.find(
            (dl) => dl.logoId === spec.logoId
          );
          add(designLogo?.logo ?? spec.baseLogo, inst.toDelivery ?? 0, spec.value);
        });
      });
    }
  });

  const result = new Map<number, StorageDraw>();
  acc.forEach((entry, logoId) => {
    if (entry.logo.logoType === LogoType.NumberLogo) {
      const digits =
        fresh?.digits.get(logoId) ?? entry.logo.numberLogosInStorage ?? [];
      const available = (digit: string) =>
        Math.max(0, digits.find((d) => d.value === digit)?.inStorage ?? 0);
      let deliveryQty = 0;
      let draw = 0;
      entry.digitDemand.forEach((qty, digit) => {
        deliveryQty += qty;
        draw += Math.min(qty, available(digit));
      });
      result.set(logoId, {
        logoId,
        title: entry.title,
        deliveryQty,
        inStorage: digits.reduce(
          (sum, d) => sum + Math.max(0, d.inStorage ?? 0),
          0
        ),
        draw,
      });
    } else {
      const inStorage = Math.max(
        0,
        fresh?.scalar.has(logoId)
          ? fresh.scalar.get(logoId)!
          : entry.logo.inStorage ?? 0
      );
      result.set(logoId, {
        logoId,
        title: entry.title,
        deliveryQty: entry.demand,
        inStorage,
        draw: Math.min(entry.demand, inStorage),
      });
    }
  });
  return result;
}

/**
 * Logos whose storage draw shrinks once the verified counts are in - the
 * cases where the rebuilt draft produces more (and may add start-up fees)
 * than the one the customer saw. Compared against the CART's own count, not
 * the API's previous figure: the cart copy is what the customer looked at,
 * and the two differ whenever the SQL row was refreshed by someone else
 * first (or the answer is a dedupe hit, where previous == fresh by
 * construction). Unchanged or increased draws never warn.
 */
export function computeStorageChangeWarnings(
  items: CartItem[],
  rows: VerifyStorageRowDto[]
): StorageChangeWarning[] {
  const fresh = freshCountsFromRows(rows);
  if (fresh.scalar.size === 0) return [];

  const before = computeStorageDraws(items);
  const after = computeStorageDraws(items, fresh);

  const warnings: StorageChangeWarning[] = [];
  const done = new Set<number>();
  rows.forEach((row) => {
    if (!fresh.scalar.has(row.logoId) || done.has(row.logoId)) return;
    done.add(row.logoId);
    const was = before.get(row.logoId);
    const now = after.get(row.logoId);
    if (!was || !now || now.draw >= was.draw) return;
    warnings.push({
      logoId: row.logoId,
      title: was.title || row.serial,
      cached: was.inStorage,
      fresh: now.inStorage,
      produced: was.draw - now.draw,
    });
  });
  return warnings;
}

function sameDigits(
  current: NumberLogoStorage[] | undefined,
  next: NumberLogoStorage[]
): boolean {
  if (!current || current.length !== next.length) return false;
  return next.every((d) => {
    const c = current.find((x) => x.value === d.value);
    return !!c && c.inStorage === d.inStorage;
  });
}

/**
 * Deep-patches the verified counts into the cart items: standalone logos'
 * baseLogo, the design's own designLogos[].logo (the draft builder prefers
 * that copy) and the per-instance baseLogo. Number logos are patched from
 * their digit rows only when the API delivered them. The storage stamp
 * (storageUpdatedAtUtc) travels with the count, so a verify that confirmed
 * the same number still refreshes "Stock updated …". Untouched items (and
 * the array itself when nothing changed) keep their identity so callers can
 * detect "nothing to do" with a reference check.
 */
export function patchStorageCounts(
  items: CartItem[],
  rows: VerifyStorageRowDto[]
): CartItem[] {
  const { scalar, digits, updatedAt } = freshCountsFromRows(rows);
  if (scalar.size === 0) return items;

  const patchLogo = <T extends BaseLogo | undefined>(logo: T): T => {
    if (!logo || !scalar.has(logo.id)) return logo;
    const nextCount = scalar.get(logo.id)!;
    const nextDigits = digits.get(logo.id);
    const nextStamp = updatedAt.get(logo.id) ?? null;
    const countSame = logo.inStorage === nextCount;
    const digitsSame = !nextDigits || sameDigits(logo.numberLogosInStorage, nextDigits);
    const stampSame = (logo.storageUpdatedAtUtc ?? null) === nextStamp;
    if (countSame && digitsSame && stampSame) return logo;
    return {
      ...logo,
      inStorage: nextCount,
      numberLogosInStorage: nextDigits ?? logo.numberLogosInStorage,
      storageUpdatedAtUtc: nextStamp,
    } as T;
  };

  let changed = false;
  const patched = items.map((item) => {
    if (isLogoItem(item)) {
      const baseLogo = patchLogo(item.baseLogo);
      if (baseLogo === item.baseLogo) return item;
      changed = true;
      return { ...item, baseLogo } as CartItemLogo;
    }
    if (isDesignItem(item)) {
      let itemChanged = false;
      const designLogos = item.design?.designLogos?.map((dl) => {
        const logo = patchLogo(dl.logo);
        if (logo === dl.logo) return dl;
        itemChanged = true;
        return { ...dl, logo };
      });
      const instances = item.instances?.map((inst) => {
        let instChanged = false;
        const logos = inst.logos?.map((l) => {
          const baseLogo = patchLogo(l.baseLogo);
          if (baseLogo === l.baseLogo) return l;
          instChanged = true;
          return { ...l, baseLogo };
        });
        if (!instChanged) return inst;
        itemChanged = true;
        return { ...inst, logos };
      });
      if (!itemChanged) return item;
      changed = true;
      return {
        ...item,
        design: designLogos ? { ...item.design, designLogos } : item.design,
        instances: instances ?? item.instances,
      } as CartItemDesign;
    }
    return item;
  });

  return changed ? patched : items;
}

/**
 * The packaging fee lines of a local draft, in the shape the draft builder
 * takes them, so the draft can be rebuilt with the SAME packaging without
 * asking NAV for the prices again. Specific packaging carries a display
 * suffix in its title that the builder re-adds, so it is stripped here.
 */
export function packagingFeesFromOrder(order: Order): PackagingFeeInput[] {
  const stripSuffix =
    order.packagingType === Packaging.SpecificPackaging;
  return (order.lines ?? [])
    .filter((line) => line.product?.productType === "Packaging")
    .map((line) => ({
      itemNo: line.product.productNo,
      price: line.unitPrice ?? line.price ?? 0,
      name:
        stripSuffix && line.title.endsWith(SPECIFIC_PACKAGING_TITLE_SUFFIX)
          ? line.title.slice(0, -SPECIFIC_PACKAGING_TITLE_SUFFIX.length)
          : line.title,
      quantity: line.quantity,
    }));
}
